import { useState } from 'react';
import { ShieldCheck, UserCheck, Lock, ArrowRight, ShieldAlert, HelpCircle } from 'lucide-react';
import { useStore } from '@/store/StoreContext';

interface LoginViewProps {
  onLoginSuccess: () => void;
}

export function LoginView({ onLoginSuccess }: LoginViewProps) {
  const { classes } = useStore();
  const [activeMode, setActiveMode] = useState<'trainee' | 'manager' | 'trainer'>('trainee');

  // Trainee login fields
  const [pNumber, setPNumber] = useState('');
  const [pin, setPin] = useState('');

  // Manager & Trainer password fields
  const [managerPassword, setManagerPassword] = useState('');
  const [trainerUsername, setTrainerUsername] = useState('Patryk.Kromer');
  const [trainerPassword, setTrainerPassword] = useState('');

  const [error, setError] = useState('');

  const handleTraineeLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const formattedPNumber = pNumber.trim().toUpperCase();
    
    // Search across ALL cohorts for the matching P-Number and PIN
    const allAgents = classes.flatMap((c) => c.agents);
    const agent = allAgents.find(
      (a) => a.pNumber.toUpperCase() === formattedPNumber && a.password === pin.trim()
    );

    if (!agent) {
      setError('Invalid P-Number or PIN code. Please check your credentials.');
      return;
    }

    localStorage.setItem('uisce-user-role', 'agent');
    localStorage.setItem('uisce-agent-id', agent.id);
    onLoginSuccess();
  };

  const handleManagerLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (managerPassword.trim() === 'UisceManagers2026!') {
      localStorage.setItem('uisce-user-role', 'manager');
      onLoginSuccess();
    } else {
      setError('Incorrect Manager password.');
    }
  };

  const handleTrainerLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (trainerPassword.trim() === '592uGkQ@ofmXhWlZoXiQ') {
      localStorage.setItem('uisce-user-role', 'trainer');
      onLoginSuccess();
    } else {
      setError('Incorrect Trainer password.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl space-y-6">
        {/* Header Icon */}
        <div className="text-center space-y-3">
          <div className="w-14 h-14 bg-blue-600/20 border border-blue-500/30 rounded-2xl mx-auto flex items-center justify-center text-blue-400">
            {activeMode === 'trainee' && <UserCheck className="w-7 h-7" />}
            {activeMode === 'manager' && <ShieldCheck className="w-7 h-7" />}
            {activeMode === 'trainer' && <Lock className="w-7 h-7" />}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Uisce Éireann</h1>
            <p className="text-xs text-slate-400 mt-1">Onboarding Portal Access</p>
          </div>
        </div>

        {/* Access Assistance Intro Banner */}
        <div className="p-3.5 rounded-2xl bg-blue-950/40 border border-blue-800/40 text-xs text-blue-200/90 leading-relaxed flex items-start gap-2.5">
          <HelpCircle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
          <div>
            Need access or assistance? If you believe you should have account details or require assistance, please reach out to <strong className="text-white">Patryk Kromer</strong> (Training Specialist).
          </div>
        </div>

        {/* Primary Role Selector Tabs */}
        <div className="p-1 bg-slate-950 rounded-2xl border border-slate-800 flex gap-1">
          <button
            type="button"
            onClick={() => { setActiveMode('trainee'); setError(''); }}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeMode === 'trainee'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Trainee Portal
          </button>

          <button
            type="button"
            onClick={() => { setActiveMode('manager'); setError(''); }}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeMode === 'manager'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Manager / Guest
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs font-semibold text-red-400 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* TRAINEE LOGIN FORM */}
        {activeMode === 'trainee' && (
          <form onSubmit={handleTraineeLogin} className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                P-Number
              </label>
              <input
                type="text"
                placeholder="e.g. P50001234"
                value={pNumber}
                onChange={(e) => setPNumber(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white font-mono focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                4-Digit PIN
              </label>
              <input
                type="password"
                placeholder="Enter PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white font-mono focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl text-xs transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer mt-2"
            >
              Access Trainee Portal <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* MANAGER / GUEST FORM */}
        {activeMode === 'manager' && (
          <form onSubmit={handleManagerLogin} className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                Manager Access Password
              </label>
              <input
                type="password"
                placeholder="Enter password"
                value={managerPassword}
                onChange={(e) => setManagerPassword(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white font-mono focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl text-xs transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer mt-2"
            >
              Access Manager View <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* TRAINER / ADMIN FORM */}
        {activeMode === 'trainer' && (
          <form onSubmit={handleTrainerLogin} className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                Trainer Username
              </label>
              <input
                type="text"
                value={trainerUsername}
                onChange={(e) => setTrainerUsername(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white font-mono focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                Trainer Password
              </label>
              <input
                type="password"
                placeholder="Enter password"
                value={trainerPassword}
                onChange={(e) => setTrainerPassword(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white font-mono focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl text-xs transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer mt-2"
            >
              Access Trainer Portal <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* Separate Discrete Admin/Trainer Link */}
        <div className="pt-4 border-t border-slate-800/80 text-center">
          {activeMode !== 'trainer' ? (
            <button
              type="button"
              onClick={() => { setActiveMode('trainer'); setError(''); }}
              className="text-xs text-slate-400 hover:text-blue-400 font-semibold transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <Lock className="w-3.5 h-3.5" /> Trainer / Admin Login
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { setActiveMode('trainee'); setError(''); }}
              className="text-xs text-slate-400 hover:text-blue-400 font-semibold transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <UserCheck className="w-3.5 h-3.5" /> Return to Trainee Portal
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
